import EventsStorage from "../../storage/EventsStorage";
import { remove, uploadData } from 'aws-amplify/storage';
import {useEffect, useState, useRef} from "react";
import config from '../../api/config';
import './Album.scss';
import {Image} from "@aws-amplify/ui-react";
import LinearProgress from '@mui/material/LinearProgress';
import { Box } from '@mui/material';
import { IconButton, Grid, Stack } from "@mui/material";
import { CalendarToday, DateRange, Today, Event, Delete, AddPhotoAlternate } from "@mui/icons-material";
import AllEventsApi from "../../api/AllEventsApi";
import {getYearAndMonthFromDate, getYearFromDate} from "../../tools/formatter";
import Alert from '@mui/material/Alert';
import CheckIcon from '@mui/icons-material/Check';
import {connect} from "react-redux";
import AlbumCoverSelector from "../AlbumCoverSelector/AlbumCoverSelector";

const Album = (props) => {
	const [items, setItems] = useState([]);
	const [selectedItems, setSelectedItems] = useState([]);
	const [imagesAreLoaded, setImagesAreLoaded] = useState(false);
	const [showSuccessAlert, setShowSuccessAlert] = useState(false);

	const [isUploading, setIsUploading] = useState(false);
	const [imagesProgress, setImagesProgress] = useState(0);
	const [cropperOpen, setCropperOpen] = useState(false);
	const [pendingLabelHandler, setPendingLabelHandler] = useState(null);
	const [coverKeys, setCoverKeys] = useState([]);

	const fileInputRef = useRef(null);

	const albumElementRef = useRef(null);

	let uploadedImagesSizes = [];
	let totalSize = 0;

	const selectItem = (item) => {
		setSelectedItems(prevState => [...prevState, item]);
	};

	const unselectItem = (deletedItem) => {
		setSelectedItems(prevState => prevState.filter(item => item !== deletedItem));
	};

	const handleAddImageClick = () => {
		const imagesStats = props.imagesStats;
		const isSubscribed = imagesStats?.subscribed && parseInt(imagesStats.subscribed) === 1;
		const currentSize = parseInt(imagesStats?.imagessize || 0);
		
		if (!isSubscribed && currentSize > config.freeUsageSize) {
			alert('You have exceeded the free usage limit. Please subscribe to continue uploading images.');
			return;
		}
		
		if (fileInputRef.current) {
			fileInputRef.current.click();
		}
	};

	const processSelect = event => {
		const imgEl = event.target;
		if(selectedItems.includes(imgEl.dataset.key)) {
			unselectItem(imgEl.dataset.key);
		} else {
			selectItem(imgEl.dataset.key);
		}
	}

	const getItems = (path) => {
		EventsStorage.listFiles(path).then((imgItems) => {
			console.log(imgItems);
			setItems(imgItems);
			setImagesAreLoaded(true);
		});
	}

	const deleteImagesHandler = () => {
		Promise.all(selectedItems.map(item => {
				return remove({'path': item})
					.then(() => item)
					.catch(error => error);
		}))
			.then(deletedItems => {
				const filteredItems = items.filter(item => {
					return !selectedItems.includes(item.key);
				});
				setItems(filteredItems);
				setSelectedItems([]);
			})
	}

	const openCropperFor = (handler) => {
		setPendingLabelHandler(() => handler);
		setCropperOpen(true);
	};

	const handleCropSelected = (transform) => {
		if (pendingLabelHandler) pendingLabelHandler(transform);
		setPendingLabelHandler(null);
	};

	const setYearLabelHandler = (transform) => {
		let item = selectedItems[0];
		let currentEvent = props.currentEvent;
		AllEventsApi.setDateLabel(currentEvent.userId, 'y', getYearFromDate(currentEvent.date), item, transform, currentEvent.id).then(response => {
			console.log(response);
			setShowSuccessAlert(true);
			setSelectedItems([]);
			setTimeout(() => setShowSuccessAlert(false), 2000);
		} ).catch(error => {
			//TODO catch
		});
	}

	const setMonthLabelHandler = (transform) => {
		let item = selectedItems[0];
		let currentEvent = props.currentEvent;
		AllEventsApi.setDateLabel(currentEvent.userId, 'm', getYearAndMonthFromDate(currentEvent.date), item, transform, currentEvent.id).then(response => {
			console.log(response);
			setShowSuccessAlert(true);
			setSelectedItems([]);
			setTimeout(() => setShowSuccessAlert(false), 2000);
		} ).catch(error => {
			//TODO catch
		});
	}

	const setDayLabelHandler = (transform) => {
		let item = selectedItems[0];
		let currentEvent = props.currentEvent;
		AllEventsApi.setDateLabel(currentEvent.userId, 'd', currentEvent.date, item, transform, currentEvent.id).then(response => {
			console.log(response);
			setShowSuccessAlert(true);
			setSelectedItems([]);
			setTimeout(() => setShowSuccessAlert(false), 2000);
		} ).catch(error => {
			//TODO catch
		});
	}

	const setEventLabelHandler = (transform) => {
		let item = selectedItems[0];
		let currentEvent = props.currentEvent;
		AllEventsApi.setDateLabel(currentEvent.userId, 'e', currentEvent.id, item, transform, currentEvent.id).then(response => {
			console.log(response);
			setShowSuccessAlert(true);
			setSelectedItems([]);
			setTimeout(() => setShowSuccessAlert(false), 2000);
		} ).catch(error => {
			//TODO catch
		});
	}

	const updateProgress = (index, loaded) => {
		//console.log(uploadedImagesSizes);
		uploadedImagesSizes[index] = loaded;
		const uploadedSum = uploadedImagesSizes.reduce((a, b) => a + b, 0);
		//console.log("Current data", uploadedSum, totalSize);
		setImagesProgress(Math.round( uploadedSum * 100 / totalSize));
	}

	const fileUploadHandler = e => {
		const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/webp', 'image/svg+xml'];
		const maxSizeInBytes = 20 * 1024 * 1024;
		let errorMsgs = [];
		for(let i = 0; i < e.target.files.length; i++) {
			const file = e.target.files[i];
			if (!validImageTypes.includes(file.type)) {
				errorMsgs.push('File is not a valid image. Please upload a JPEG, PNG, GIF, BMP, WebP, or SVG image.');
			}
			if (file.size > maxSizeInBytes) {
				errorMsgs.push(`File size exceeds 20MB. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB.`);
			}
			if (errorMsgs.length) {
				errorMsgs.forEach(msg => alert(msg));
				if (fileInputRef.current) {
					fileInputRef.current.value = '';
				}
				return;
			}
		}

		setIsUploading(true);
		//console.log('Setting total images', e.target.files.length);
		let promises = [];
		for(let i = 0; i < e.target.files.length; i++) {
			const file = e.target.files[i];
			const key = props.path + '/' + file.size + '-' + file.name;
			uploadedImagesSizes[i] = 0;
			totalSize += file.size;
			promises.push(uploadData({key: key, data: file, options: {
				onProgress:  ({ transferredBytes }) => {
					updateProgress(i, transferredBytes);
					//console.log(`Uploaded: ${progress.loaded}/${progress.total}`);
				},
				errorCallback: (err) => {
					console.error('Unexpected error while uploading', err);
				}
			}}).result);
		}
		Promise.all(promises).then(() => {
			e.target.value = null;
			setIsUploading(false);
			getItems(props.path);
			uploadedImagesSizes = [];
			totalSize = 0;
			console.log('Everything is finished');
		})
	};

	useEffect(() => {
		if (props.path && props.eventIsLoaded) {
			getItems(props.path);
		}
	}, [props.path, props.eventIsLoaded]);

	useEffect(() => {
		const { currentEvent } = props;
		if (!currentEvent?.userId || !currentEvent?.id) return;
		AllEventsApi.getLabelsByEvent(currentEvent.userId, currentEvent.id).then(response => {
			const keys = (response || []).map(item => item.userimage).filter(Boolean);
			if (keys.length) setCoverKeys(keys);
		}).catch(() => {});
	}, [props.currentEvent?.id]);

	useEffect(() => {
		let albumElement = null;
		if (albumElementRef.current) {
			albumElement = albumElementRef.current;
		}
		return () => {
			console.log("CLEANUP");
			if (albumElement) {
				console.log("CLEANUP ALBUM");
				albumElement.innerHtml = '';
				setItems([]);
			};
		}

	}, []);

	return (
		<Box ref={albumElementRef} className="album">
			<AlbumCoverSelector
				open={cropperOpen}
				onClose={() => setCropperOpen(false)}
				imageSrc={items.find(item => item.key === selectedItems[0])?.path}
				aspect={16 / 9}
				onCoverSelected={handleCropSelected}
			/>
			<Grid container spacing={2}>
				<Grid item xs={1}>
					<Stack 
						direction="column" 
						spacing={1}
						alignItems="center"
						sx={{ 
							position: 'sticky',
							top: 20,
							display: isUploading ? 'none' : 'flex'
						}}
					>
						<IconButton 
							onClick={deleteImagesHandler} 
							className="img-btn"
							title="Delete"
							sx={{ 
								display: selectedItems.length > 0 ? 'inline-flex' : 'none',
								'&:hover': { color: '#BCE3DF' },
								width: 56,
								height: 56
							}}
						>
							<Delete sx={{ fontSize: 32 }} />
						</IconButton>
						<IconButton 
							onClick={() => openCropperFor(setYearLabelHandler)} 
							className="img-btn"
							title="Set year label"
							sx={{ 
								display: selectedItems.length === 1 ? 'inline-flex' : 'none',
								'&:hover': { color: '#BCE3DF' },
								width: 56,
								height: 56
							}}
						>
							<CalendarToday sx={{ fontSize: 32 }} />
						</IconButton>
						<IconButton 
							onClick={() => openCropperFor(setMonthLabelHandler)} 
							className="img-btn"
							title="Set month label"
							sx={{ 
								display: selectedItems.length === 1 ? 'inline-flex' : 'none',
								'&:hover': { color: '#BCE3DF' },
								width: 56,
								height: 56
							}}
						>
							<DateRange sx={{ fontSize: 32 }} />
						</IconButton>
						<IconButton 
							onClick={() => openCropperFor(setDayLabelHandler)} 
							className="img-btn"
							title="Set day label"
							sx={{ 
								display: selectedItems.length === 1 ? 'inline-flex' : 'none',
								'&:hover': { color: '#BCE3DF' },
								width: 56,
								height: 56
							}}
						>
							<Today sx={{ fontSize: 32 }} />
						</IconButton>
						<IconButton 
							onClick={() => openCropperFor(setEventLabelHandler)} 
							className="img-btn"
							title="Set event label"
							sx={{ 
								display: selectedItems.length === 1 ? 'inline-flex' : 'none',
								'&:hover': { color: '#BCE3DF' },
								width: 56,
								height: 56
							}}
						>
							<Event sx={{ fontSize: 32 }} />
						</IconButton>
						<IconButton 
							className="img-btn"
							title="Add images"
							onClick={handleAddImageClick}
							sx={{ 
								display: selectedItems.length > 0 ? 'none' : 'inline-flex',
								'&:hover': { color: '#BCE3DF' },
								width: 56,
								height: 56
							}}
						>
							<AddPhotoAlternate sx={{ fontSize: 32 }} />
						</IconButton>
						<input 
							ref={fileInputRef} 
							style={{ display: 'none' }} 
							id="uploadInput" 
							type="file" 
							name="myFiles" 
							multiple 
							onChange={fileUploadHandler} 
						/>
					</Stack>
				</Grid>
				<Grid item xs={11}>
					{isUploading && (
						<Box sx={{ width: '100%', padding: '10px 0', mb: 2 }}>
							<LinearProgress variant="determinate" value={imagesProgress} />
						</Box>
					)}
					{(!props.eventIsLoaded || !imagesAreLoaded) && (
						<Box sx={{ width: '100%', padding: '10px 0', mb: 2 }}>
							<LinearProgress color="success" />
						</Box>
					)}
					{showSuccessAlert && (
						<Alert icon={<CheckIcon fontSize="inherit" />} severity="success" sx={{ mb: 2 }}>
							Label set successfully!
						</Alert>
					)}
					{items.length > 0 && props.eventIsLoaded && imagesAreLoaded && items.map(item => {
						const isCover = coverKeys.includes(item.key);
						return <Image key={item.key} data-key={item.key} src={config.thumbnailLambdaUrl + '?key=/' + item.key} onClick={processSelect} className={selectedItems.includes(item.key) ? 'selected' : ''} style={isCover ? { border: '3px solid blue' } : {}} alt='' />
					})}
				</Grid>
			</Grid>
		</Box>
	)
};

const mapStateToProps = state => {
	return {
		imagesStats: state.eventsData.imagesStats
	};
};

export default connect(mapStateToProps)(Album);